import clsx from 'clsx';
import { useField, useFormikContext } from 'formik';
import type { FC, MouseEvent } from 'react';
import { useState } from 'react';
import { TFA_INPUT_TEST_IDS } from './constants';
import type { ISimpleInputProps } from './types';
import Spinner from '~/components/actions/Button/components/Spinner';
import InputError from '~/components/feedback/InputError';
import useTimeout from '~/hooks/useTimeout';
import { getInputSizeClasses } from '~/utils/form';
import useSearchParams from '~/hooks/useSearchParams';
import { getApiURL } from '~/utils/api';
import { linkUnderlineClasses } from '~/utils';
import './style.css';

const TFAInput: FC<ISimpleInputProps> = ({ name, label, width, type, required, ...rest }) => {
	const [field, meta] = useField(name);
	const { submitCount } = useFormikContext();
	const isError = (meta.touched || submitCount > 0) && !!meta.error;
	const [resendError, setResendError] = useState(false);
	const [resendSuccess, setResendSuccess] = useState(false);
	const [blockResend, setBlockResend] = useState(false);
	const [isResending, setIsResending] = useState(false);
	const searchParams = useSearchParams();

	const clearResendMessages = () => {
		setResendSuccess(false);
		setResendError(false);
	};
	const clearResendBlock = () => {
		setBlockResend(false);
	};

	useTimeout(clearResendMessages, resendSuccess === true || resendError === true ? 8000 : null);
	useTimeout(clearResendBlock, blockResend === true ? 30000 : null);

	const handleResendRequested = async (event: MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();

		if (blockResend) {
			return;
		}

		clearResendMessages();

		setIsResending(true);
		const response = await fetch(`${getApiURL()}/resend`, {
			method: 'POST',
			body: JSON.stringify({
				entryUuid: searchParams.get('entryUuid'),
			}),
			headers: {
				'Content-Type': 'application/json',
			},
		});

		if (response.ok) {
			setBlockResend(true);
			setResendSuccess(true);
		} else {
			setResendError(true);
		}
		setIsResending(false);
	};

	const handleEditPressed = async (event: MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		window.location.href = '/';
	};

	return (
		<div className={clsx('w-full mx-auto', getInputSizeClasses(width))}>
			<div
				className="simple-input-container mx-auto rounded-tl-input rounded-tr-input relative h-input w-full"
				data-testid={TFA_INPUT_TEST_IDS.ROOT}
			>
				<input
					{...rest}
					{...field}
					type={type}
					className={clsx(
						'simple-input-input peer bg-transparent placeholder-transparent text-base w-full outline-none pl-5 pr-5 pt-6 pb-1 h-full',
						isError ? 'is-error' : '',
					)}
					id={name}
					placeholder=" "
					data-testid={TFA_INPUT_TEST_IDS.INPUT}
				/>
				<label
					htmlFor={name}
					className="simple-input-label pointer-events-none transition-all peer-focus:top-3 top-3 peer-focus:text-xs text-xs absolute left-5 peer-placeholder-shown:top-6 peer-placeholder-shown:font-large-body"
					data-testid={TFA_INPUT_TEST_IDS.LABEL}
				>
					{label}
					{required && !label.endsWith('*') ? <sup>*</sup> : null}
				</label>
				<div className="simple-input-border absolute -bottom-[2px] left-0 w-full h-[2px] transition-colors duration-200" />
				{!resendError && (
					<InputError name={name} isError={isError}>
						{meta.error}
					</InputError>
				)}
			</div>
			{resendError && (
				<p className="tfa-error-message font-small-body mt-1">
					Failed to send a new code to your phone. Please try again later.
				</p>
			)}
			{resendSuccess && (
				<p className="tfa-success-message font-small-body mt-1">
					A new code has been successfully sent to {searchParams.get('mobile')}. Please
					enter it above.
				</p>
			)}
			<p className="mt-1 text-sm text-center max-w-[400px] mx-auto">
				Please enter the code we sent to {searchParams.get('mobile')}.{' '}
				<button
					type="button"
					className={clsx(
						'tfa-input-links nav-link block mx-auto',
						blockResend ? 'cursor-not-allowed' : 'cursor-pointer',
						linkUnderlineClasses,
					)}
					disabled={blockResend || isResending}
					onClick={handleResendRequested}
				>
					{isResending && <Spinner className="mr-1" />} Click here to resend.
				</button>
				Wrong number?{' '}
				<button
					type="button"
					className={clsx('tfa-input-links nav-link', linkUnderlineClasses)}
					onClick={handleEditPressed}
				>
					Go back and start again
				</button>
				.
			</p>
		</div>
	);
};

export default TFAInput;
